import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {CommentService, DocumentService, DocumentThread, DocumentThreadService} from 'client';
import {useCollabId} from 'components/DSYS/CollabProvider';
import {findMentions} from 'components/DSYS/editor/utils/findMentions';
import {sanitizeCommentContent} from 'helpers/commentContent';
import {createContext, ReactNode, useCallback, useContext} from 'react';
import {getService} from 'utilities';

/**
 * Parameters for creating a new thread with a comment
 */
export interface CreateThreadParams {
  comment: string;
  employeeId: string;
  attachedText?: string;
}

interface ThreadsContextType {
  threads: DocumentThread[] | undefined;
  isLoading: boolean;
  refetch?: () => void;
  /**
   * Create a new thread with a comment.
   * Returns the thread ID on success.
   */
  createThread?: (params: CreateThreadParams) => Promise<string>;
}

const ThreadsContext = createContext<ThreadsContextType | null>(null);

/**
 * Hook to access the threads context.
 * Returns null if not within a DocumentThreadsProvider.
 */
export const useThreadsContext = (): ThreadsContextType | null => {
  return useContext(ThreadsContext);
};

/**
 * Provider that fetches threads from the document API.
 * Use this for collaborative documents.
 *
 * Automatically:
 * - Fetches threads from document API using documentId
 * - Provides thread creation via DocumentThreadService
 *
 * Live refreshes arrive as `thread` server messages (see helpers/serverMessageHandler.ts).
 *
 * @param documentId - Optional document ID. If not provided, uses useCollabId() from context.
 *                     Pass this when the provider is outside the CollabProvider.
 */
export const DocumentThreadsProvider = ({
  children,
  documentId: documentIdProp,
}: {
  children: ReactNode;
  documentId?: string;
}) => {
  const collabDocumentId = useCollabId();
  const documentId = documentIdProp || collabDocumentId;
  const queryClient = useQueryClient();
  const {
    data: threads,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['document', 'threads', documentId],
    queryFn: documentId
      ? ({signal}) => {
          const request =
            getService(DocumentService).getThreadsApiV1DocumentDocumentIdThreadsGet(documentId);
          signal.addEventListener('abort', () => {
            request.cancel();
          });
          return request;
        }
      : async () => Promise.resolve([]),
    enabled: Boolean(documentId),
  });

  // Mutation for creating document threads
  const {mutateAsync: createDocumentThread} = useMutation({
    mutationFn: async ({comment, employeeId}: CreateThreadParams) => {
      if (!documentId) {
        throw new Error('documentId is required for creating document threads');
      }

      // Create a new document thread
      const newThread = await getService(DocumentThreadService).createApiV1DocumentthreadPost({
        created_at: new Date().toISOString(),
        employee_id: employeeId,
        document_id: documentId,
      });

      // Create a comment on the new thread
      const mentions = findMentions(comment);
      const mentionUserIds = mentions.map((mention) => mention.id);
      await getService(CommentService).createCommentReplyApiV1CommentReplyPost({
        content: comment,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        document_thread_id: newThread.id,
        mentioned_user_ids: mentionUserIds,
      });

      return newThread.id;
    },
    onSuccess: () => {
      // Invalidate threads to refetch
      queryClient.invalidateQueries({
        queryKey: ['document', 'threads', documentId],
      });
    },
  });

  const createThread = useCallback(
    (params: CreateThreadParams) => {
      const sanitizedComment = sanitizeCommentContent(params.comment);
      if (!sanitizedComment) {
        throw new Error('comment content is required');
      }

      return createDocumentThread({...params, comment: sanitizedComment});
    },
    [createDocumentThread]
  );

  return (
    <ThreadsContext.Provider value={{threads, isLoading, refetch, createThread}}>
      {children}
    </ThreadsContext.Provider>
  );
};
